import { useCallback, useMemo } from 'react';

import { aiAgentsApi, AiAgentListItem } from 'apis/aiAgents';
import { useServerStorage } from 'utils/useServerStorage';

interface UseDraftAiAgentsOptions {
  /**
   * Whether to skip loading the AI agents. The default agent ids and the
   * functions to set them are resolved either way.
   */
  skip?: boolean;
}

export interface UseDraftAiAgentsResult {
  /** The AI agents that can draft replies. */
  agents: AiAgentListItem[];
  /** The AI agent id to draft with, user preference before organization one. */
  defaultAgentId: string | undefined;
  /** The AI agent id the user set as their own default. */
  userDefaultAgentId: string | undefined;
  /** The AI agent id the organization set as the default for everyone. */
  orgDefaultAgentId: string | undefined;
  /** Sets the AI agent the user drafts with by default. */
  setUserDefaultAgentId: (agentId: string) => void;
  /** Sets the AI agent the organization drafts with by default. */
  setOrgDefaultAgentId: (agentId: string) => void;
  isLoading: boolean;
  isError: boolean;
}

/** A hook for the AI agents that can draft replies and the default ones. */
export function useDraftAiAgents({
  skip,
}: UseDraftAiAgentsOptions = {}): UseDraftAiAgentsResult {
  const { data, isLoading, isError } = aiAgentsApi.useReadAiAgentsListQuery(
    {},
    { skip },
  );
  const { data: userStorageData, set: setUserStorage } =
    useServerStorage('user');
  const { data: organizationStorageData, set: setOrganizationStorage } =
    useServerStorage('organization');

  const userDefaultAgentId = userStorageData.agent_copilot?.defaultDraftAgentId;
  const orgDefaultAgentId =
    organizationStorageData.agent_copilot?.defaultDraftAgentId;

  const setUserDefaultAgentId = useCallback(
    (agentId: string) =>
      setUserStorage({
        agent_copilot: (draft) => {
          draft.defaultDraftAgentId = agentId;
        },
      }),
    [setUserStorage],
  );

  const setOrgDefaultAgentId = useCallback(
    (agentId: string) =>
      setOrganizationStorage({
        agent_copilot: (draft) => {
          draft.defaultDraftAgentId = agentId;
        },
      }),
    [setOrganizationStorage],
  );

  const agents = useMemo(
    () =>
      (data?.data?.agents ?? [])
        .filter((agent) => agent.variant !== 'automationbot')
        .reverse(),
    [data],
  );

  return {
    agents,
    defaultAgentId: userDefaultAgentId ?? orgDefaultAgentId,
    userDefaultAgentId,
    orgDefaultAgentId,
    setUserDefaultAgentId,
    setOrgDefaultAgentId,
    isLoading,
    isError,
  };
}
